/**
 * APS Product Card — variant popover + quantity + custom mini-toast.
 *
 * Wat dit doet:
 *  - Popover open/close voor multi-variant kaarten
 *  - Quantity +/- knoppen voor single-variant forms
 *  - Variant-pick: set [name=id].value + form.requestSubmit(hidden)
 *  - Na een successful add vanuit een APS card: toont een compacte
 *    mini-toast onderaan (eigen component, niet de Loess cart-
 *    notification). Cart-drawer blijft dicht — Joey wil dat de
 *    gebruiker door kan scrollen/shoppen zonder iets weg te klikken.
 *
 * De add-ajax loopt via <loess-product-form redirect-type="drawer">
 * van het thema zelf: Loess renders de drawer-items (counter + badge
 * update) maar opent de drawer niet. Perfect voor doorshoppen.
 */
(function () {
  'use strict';

  if (window.__apsCardInit) return;
  window.__apsCardInit = true;

  let lastApsAddAt = 0;
  let lastApsProduct = null;
  let lastSuccessTarget = null;
  const APS_ADD_WINDOW_MS = 2000;
  const APS_SUCCESS_DURATION_MS = 1800;

  function flashSuccess(btn) {
    if (!btn || btn.dataset.apsRestoring === '1') return;
    if (btn.offsetParent === null && !btn.classList.contains('aps-pdp-buy__submit')) return;
    // We vervangen NIET de hele innerHTML — dat blaast o.a. de Loess
    // spinner weg en breekt de loading-state-toggle bij de volgende klik.
    // In plaats daarvan swappen we alleen label.textContent en icon.innerHTML.
    const label =
      btn.querySelector('.aps-pdp-buy__label') ||
      btn.querySelector('.aps-card__atc-label') ||
      btn.querySelector('.btn-text') ||
      btn.querySelector(':scope > span:not(.spinner)');
    const icon =
      btn.querySelector('.aps-pdp-buy__icon') ||
      btn.querySelector('.aps-card__atc-icon') ||
      btn.querySelector('.btn-icon');
    if (!label) return;
    const originalLabel = label.textContent;
    const originalIcon = icon ? icon.innerHTML : '';
    btn.dataset.apsRestoring = '1';
    btn.classList.add('is-success');
    label.textContent = 'Toegevoegd';
    if (icon) icon.innerHTML = '<span class="aps-success-check" aria-hidden="true">&#10003;</span>';
    setTimeout(() => {
      label.textContent = originalLabel;
      if (icon) icon.innerHTML = originalIcon;
      btn.classList.remove('is-success');
      delete btn.dataset.apsRestoring;
    }, APS_SUCCESS_DURATION_MS);
  }

  function closeAllPanels(exceptWrap) {
    document.querySelectorAll('[data-aps-variant-wrap]').forEach((wrap) => {
      if (wrap === exceptWrap) return;
      const toggle = wrap.querySelector('[data-aps-variant-toggle]');
      const panel = wrap.querySelector('[data-aps-variants-panel]');
      if (panel) panel.setAttribute('hidden', '');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      wrap.classList.remove('is-open');
    });
  }

  function onToggleClick(event) {
    const toggle = event.target.closest('[data-aps-variant-toggle]');
    if (!toggle) return;
    event.preventDefault();
    const wrap = toggle.closest('[data-aps-variant-wrap]');
    if (!wrap) return;
    const panel = wrap.querySelector('[data-aps-variants-panel]');
    const isOpen = !panel.hasAttribute('hidden');
    closeAllPanels(isOpen ? null : wrap);
    if (isOpen) {
      panel.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', 'false');
      wrap.classList.remove('is-open');
    } else {
      panel.removeAttribute('hidden');
      toggle.setAttribute('aria-expanded', 'true');
      wrap.classList.add('is-open');
    }
  }

  function onDocClick(event) {
    if (event.target.closest('[data-aps-variant-wrap]')) return;
    closeAllPanels(null);
  }

  function cardContext(el) {
    const card = el?.closest?.('.aps-card');
    if (!card) return null;
    const title = card.querySelector('.aps-card__title')?.textContent?.trim();
    const img = card.querySelector('.aps-card__img--primary')?.getAttribute('src');
    return { title, img };
  }

  function pdpContext() {
    const title =
      document.querySelector('.aps-pdp-title__heading')?.textContent?.trim() ||
      document.querySelector('.main-product h1')?.textContent?.trim() ||
      document.querySelector('.seo-h1')?.textContent?.trim();
    const mediaImg =
      document.querySelector('.main-product__media-gallery__inner-media img') ||
      document.querySelector('.main-product__media-gallery img') ||
      document.querySelector('.sticky-atc__img');
    const img = mediaImg?.currentSrc || mediaImg?.src;
    return title ? { title, img } : null;
  }

  function onVariantPick(event) {
    const btn = event.target.closest('[data-aps-variant-pick]');
    if (!btn) return;
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    event.preventDefault();
    const variantId = btn.dataset.variantId;
    const form = btn.closest('form');
    if (!form || !variantId) return;
    const idInput = form.querySelector('[name="id"]');
    if (idInput) {
      idInput.disabled = false;
      idInput.value = variantId;
    }
    closeAllPanels(null);
    lastApsAddAt = Date.now();
    lastApsProduct = cardContext(btn);
    const wrap = btn.closest('[data-aps-variant-wrap]');
    const toggle = wrap?.querySelector('[data-aps-variant-toggle]');
    lastSuccessTarget = toggle || btn.closest('.aps-card')?.querySelector('.aps-card__atc');
    const submitBtn = form.querySelector('[data-aps-real-submit]');
    if (submitBtn && form.requestSubmit) {
      form.requestSubmit(submitBtn);
    } else if (submitBtn) {
      submitBtn.click();
    } else if (form.requestSubmit) {
      form.requestSubmit();
    }
  }

  function onSingleFormSubmit(event) {
    const form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    const cardForm = form.classList.contains('aps-card__form') ? form : null;
    if (cardForm) {
      lastApsAddAt = Date.now();
      lastApsProduct = cardContext(cardForm);
      const vis = cardForm.querySelector('.aps-card__atc:not(.aps-card__atc--hidden-submit)');
      if (vis) lastSuccessTarget = vis;
      return;
    }

    const loessForm = form.closest('loess-product-form');
    if (loessForm) {
      const btn = loessForm.querySelector('.aps-pdp-buy__submit, [data-atc-button]');
      if (btn) {
        lastApsAddAt = Date.now();
        lastApsProduct = pdpContext();
        lastSuccessTarget = btn;
      }
    }
  }

  function onQtyClick(event) {
    const btn = event.target.closest('[data-aps-qty-minus], [data-aps-qty-plus]');
    if (!btn) return;
    event.preventDefault();
    const input = btn.parentElement?.querySelector('.aps-card__qty-input');
    if (!input) return;
    let val = parseInt(input.value, 10) || 1;
    if (btn.hasAttribute('data-aps-qty-plus')) val += 1;
    else val = Math.max(1, val - 1);
    input.value = String(val);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function ensureToastEl() {
    let el = document.getElementById('aps-card-toast');
    if (el) return el;
    el = document.createElement('div');
    el.id = 'aps-card-toast';
    el.className = 'aps-card-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.innerHTML = `
      <img class="aps-card-toast__img" alt="" width="40" height="40">
      <div class="aps-card-toast__body">
        <div class="aps-card-toast__title">Toegevoegd aan winkelwagen</div>
        <div class="aps-card-toast__product"></div>
      </div>
      <button type="button" class="aps-card-toast__link" data-aps-toast-open-drawer>Bekijk &rarr;</button>
    `;
    document.body.appendChild(el);
    return el;
  }

  function offsetForStickyAtc() {
    // Als de sticky-atc-bar op de pagina staat, rekenen we zijn hoogte
    // erbij zodat de toaster netjes erboven zweeft i.p.v. eronder
    // verdwijnt. Loess rendered de bar ook op desktop (position: fixed).
    const atc = document.querySelector('.sticky-atc-bar, [data-sticky-atc], .sm-sticky-atc');
    if (!atc) return 0;
    const rect = atc.getBoundingClientRect();
    const visible = rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
    return visible ? Math.round(rect.height + 12) : 0;
  }

  let hideTimer = 0;
  function showToast(product) {
    const el = ensureToastEl();
    const img = el.querySelector('.aps-card-toast__img');
    const name = el.querySelector('.aps-card-toast__product');
    if (product?.img) { img.src = product.img; img.style.display = ''; }
    else { img.removeAttribute('src'); img.style.display = 'none'; }
    name.textContent = product?.title || '';
    const atcOffset = offsetForStickyAtc();
    el.style.bottom = atcOffset > 0 ? atcOffset + 'px' : '';
    el.classList.add('is-visible');
    clearTimeout(hideTimer);
    hideTimer = setTimeout(() => el.classList.remove('is-visible'), 3500);
  }

  async function syncDrawerCart() {
    // Loess' renderCartItems leest state.items van /cart/add.js (dat
    // geeft slechts het toegevoegde item terug — geen items array), en
    // zet dus foutief is-empty=true op de drawer. We halen de echte
    // cart-staat op en repareren de class + tellers.
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}cart.js`, { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const itemCount = Array.isArray(cart?.items)
        ? cart.items.filter(i => !(i.properties && i.properties._goodie === 'true')).length
        : 0;
      const isEmpty = itemCount === 0;
      document.querySelectorAll('loess-cart-drawer-items, loess-cart-items').forEach((el) => {
        el.classList.toggle('is-empty', isEmpty);
        // Loess zet de "bottom" (cart totals) zichtbaarheid op de next sibling.
        const bottom = el.parentElement?.nextElementSibling;
        if (bottom && bottom.classList) bottom.classList.toggle('hide', isEmpty);
      });
    } catch (err) {
      // stil falen — toast is al getoond
    }
  }

  function onCartUpdated() {
    if (Date.now() - lastApsAddAt > APS_ADD_WINDOW_MS) return;
    const product = lastApsProduct;
    const successTarget = lastSuccessTarget;
    lastApsAddAt = 0;
    lastApsProduct = null;
    lastSuccessTarget = null;
    // Toast tonen wanneer we context hebben (card of PDP). Op PDP geven
    // knop-pill + toast samen duidelijke feedback (niet dubbelop — de
    // knop is kort-flits, toast is een persistente confirmation).
    if (product) showToast(product);
    flashSuccess(successTarget);
    syncDrawerCart();
  }

  let toastOpenPending = false;
  function onToastBekijkClick(event) {
    const btn = event.target.closest('[data-aps-toast-open-drawer]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    if (toastOpenPending) return;

    const toast = document.getElementById('aps-card-toast');
    if (toast) toast.classList.remove('is-visible');

    // Klik de bestaande cart-icon in de header aan i.p.v. direct
    // drawer.open = true te zetten. Dat voorkomt een Loess feedback-
    // loop waarbij <button is="loess-button" aria-controls="CartDrawer">
    // een 'expandable-html-element:open' event vangt en de drawer
    // opnieuw opent, met een zichtbaar flicker tot gevolg.
    const cartButton =
      document.querySelector('#HeaderCartIcon [aria-controls="CartDrawer"]') ||
      document.querySelector('[aria-controls="CartDrawer"]');
    const drawer = document.getElementById('CartDrawer');

    if (drawer && (drawer.open === true || drawer.hasAttribute('open'))) return;

    toastOpenPending = true;
    if (cartButton && typeof cartButton.click === 'function') {
      cartButton.click();
    } else if (drawer) {
      drawer.open = true;
    } else {
      window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
    }
    setTimeout(() => { toastOpenPending = false; }, 500);
  }

  document.addEventListener('click', (e) => {
    onToggleClick(e);
    onVariantPick(e);
    onQtyClick(e);
    onToastBekijkClick(e);
    onDocClick(e);
  });

  document.addEventListener('submit', onSingleFormSubmit, true);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAllPanels(null);
  });

  document.documentElement.addEventListener('cart:updated', onCartUpdated);
})();
